import { useState, useEffect, useCallback } from 'react'
import posthog from 'posthog-js'
import { submitListingVote } from '../services/api'

const VOTE_STORAGE_KEY = 'propdna_listing_votes'

function getLocalVotes() {
  try {
    return JSON.parse(localStorage.getItem(VOTE_STORAGE_KEY) || '{}')
  } catch { return {} }
}

function setLocalVote(propertyId, vote) {
  try {
    const votes = getLocalVotes()
    votes[propertyId] = vote
    localStorage.setItem(VOTE_STORAGE_KEY, JSON.stringify(votes))
  } catch { /* quota */ }
}

function getVoterId() {
  try {
    return posthog.get_distinct_id?.() || null
  } catch { return null }
}

const VOTE_OPTIONS = [
  { value: 'yes', label: 'Yes', icon: 'thumb_up', color: 'emerald' },
  { value: 'no', label: 'No', icon: 'thumb_down', color: 'red' },
  { value: 'lower_price', label: 'Lower', icon: 'trending_down', color: 'amber' },
]

/**
 * ListingVoteWidget — compact poll for browse cards and full for reports.
 *
 * Props:
 *   propertyId     - Unique property identifier
 *   listPrice      - Asking price (shown in the question)
 *   city           - City name (passed to vote record)
 *   propertyType   - 'house' or 'condo'
 *   compact        - If true, renders mini inline variant for browse cards
 *   initialAggregates - Pre-fetched { yes_count, no_count, lower_price_count, total_votes, user_vote }
 */
export default function ListingVoteWidget({
  propertyId,
  listPrice,
  city,
  propertyType,
  compact = false,
  initialAggregates,
}) {
  const [userVote, setUserVote] = useState(() => {
    if (initialAggregates?.user_vote) return initialAggregates.user_vote
    return getLocalVotes()[propertyId] || null
  })
  const [aggregates, setAggregates] = useState(initialAggregates || null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (initialAggregates) {
      setAggregates(initialAggregates)
      if (initialAggregates.user_vote) setUserVote(initialAggregates.user_vote)
    }
  }, [initialAggregates])

  const handleVote = useCallback(async (vote) => {
    if (submitting) return
    const voterId = getVoterId()
    if (!voterId) return

    setSubmitting(true)
    setUserVote(vote)
    setLocalVote(propertyId, vote)

    posthog.capture('listing_vote_submitted', {
      property_id: propertyId,
      vote,
      city,
      property_type: propertyType,
      compact,
    })

    try {
      const result = await submitListingVote({
        property_id: propertyId,
        voter_id: voterId,
        vote,
        city,
        property_type: propertyType,
      })
      setAggregates(result)
    } catch {
      // Keep optimistic UI; aggregates will refresh on next load
    } finally {
      setSubmitting(false)
    }
  }, [propertyId, city, propertyType, compact, submitting])

  const hasVoted = !!userVote
  const total = aggregates?.total_votes || 0

  if (compact) {
    return (
      <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
        {total > 0 && (
          <div className="mb-2">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">Community pulse</span>
              <span className="text-xs text-slate-400 dark:text-slate-500">{total} vote{total !== 1 ? 's' : ''}</span>
            </div>
            <VoteResultBar aggregates={aggregates} userVote={userVote} />
          </div>
        )}
        {!hasVoted && (
          <div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
              Would you buy at list price?
            </p>
            <div className="flex gap-1.5">
              {VOTE_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={(e) => { e.stopPropagation(); handleVote(opt.value) }}
                  disabled={submitting}
                  className={`flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-semibold transition-all
                    border border-slate-200 dark:border-slate-700 hover:border-primary hover:text-primary
                    text-slate-600 dark:text-slate-400 disabled:opacity-50`}
                >
                  <span className="material-symbols-outlined text-sm">{opt.icon}</span>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  // Full variant (property report page)
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 sm:p-6">
      <div className="flex items-center gap-2 mb-4">
        <span className="material-symbols-outlined text-primary text-xl">how_to_vote</span>
        <h3 className="text-base font-bold text-navy-deep dark:text-white">Community Pulse</h3>
        {total > 0 && (
          <span className="text-xs text-slate-400 dark:text-slate-500 ml-auto">{total} vote{total !== 1 ? 's' : ''}</span>
        )}
      </div>

      {!hasVoted ? (
        <div>
          <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
            Would you buy at the current list price?
          </p>
          <div className="flex gap-2">
            {VOTE_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => handleVote(opt.value)}
                disabled={submitting}
                className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold transition-all
                  border-2 border-slate-200 dark:border-slate-700 hover:border-primary hover:text-primary hover:bg-primary/5
                  text-slate-600 dark:text-slate-400 disabled:opacity-50`}
              >
                <span className="material-symbols-outlined text-lg">{opt.icon}</span>
                {opt.label === 'Lower' ? 'Only lower' : opt.label}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
            Thanks for voting! Here's what others think:
          </p>
          <VoteResultBar aggregates={aggregates} userVote={userVote} full />
          <button
            onClick={() => { setUserVote(null); }}
            className="mt-3 text-xs text-slate-400 hover:text-primary transition-colors"
          >
            Change vote
          </button>
        </div>
      )}
    </div>
  )
}

function VoteResultBar({ aggregates, userVote, full = false }) {
  const total = aggregates?.total_votes || 0
  if (!total) return null

  const segments = [
    { key: 'yes', count: aggregates.yes_count || 0, label: 'Yes', color: 'bg-emerald-500' },
    { key: 'no', count: aggregates.no_count || 0, label: 'No', color: 'bg-red-500' },
    { key: 'lower_price', count: aggregates.lower_price_count || 0, label: 'Lower', color: 'bg-amber-500' },
  ]

  return (
    <div>
      <div className={`flex rounded-full overflow-hidden ${full ? 'h-3' : 'h-2'}`}>
        {segments.map(seg => {
          const pct = total > 0 ? (seg.count / total) * 100 : 0
          if (pct === 0) return null
          return (
            <div
              key={seg.key}
              className={`${seg.color} transition-all duration-500`}
              style={{ width: `${pct}%` }}
            />
          )
        })}
      </div>
      {full && (
        <div className="flex justify-between mt-2">
          {segments.map(seg => {
            const pct = total > 0 ? Math.round((seg.count / total) * 100) : 0
            const isUserChoice = userVote === seg.key
            return (
              <span
                key={seg.key}
                className={`text-xs ${isUserChoice ? 'font-bold text-navy-deep dark:text-white' : 'text-slate-500 dark:text-slate-400'}`}
              >
                {seg.label} {pct}%{isUserChoice && ' ✓'}
              </span>
            )
          })}
        </div>
      )}
    </div>
  )
}

/**
 * Compact vote count badge for browse cards (shows after voting or for pre-loaded aggregates).
 * Useful as a standalone indicator without the full voting UI.
 */
export function VoteCountBadge({ totalVotes }) {
  if (!totalVotes) return null
  return (
    <span className="inline-flex items-center gap-1 text-xs text-slate-400 dark:text-slate-500">
      <span className="material-symbols-outlined text-xs">how_to_vote</span>
      {totalVotes}
    </span>
  )
}
